import { DockviewApi, DockviewPanelApi } from "dockview-react";
import React, { useCallback, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { MdHeadphones } from "react-icons/md";
import { api } from "./utils/api";
import { useReadAloud, ReadableItem } from "./utils/useReadAloud";
import { ReadAloudBar } from "./utils/ReadAloudBar";
import { useCtrlDMark } from "./utils/useCtrlDMark";
import { HighlightedText, Mark } from "./utils/HighlightedText";
import { RedditFeedTimeline } from "./RedditFeedTimeline";
import { formatIsoTimestampNYC } from "./utils/utils";
import { addFloatingPanel } from "./utils/DockerviewSettings";
import { TickerLink } from "./TickerActionMenu";

interface ITickerChange {
  // Recent events (generated within the last week): a single live change.
  change_rel?: number;
  // Older events: historical previous-day + premarket changes as of the generation date,
  // computed the same way as the Simulated Watchlist.
  prev_day_change_rel?: number;
  premarket_change_rel?: number;
}

interface IRedditEvent {
  id: number;
  company: string;
  company_marks: Mark[];
  event: string;
  event_marks: Mark[];
  summary: string;
  summary_marks: Mark[];
  date: string;
  num_comments: number;
  generation_timestamp: string | null;
  mark: number;
  parent_ids?: number[];
  // only populated for results returned by the target-date search
  ticker_changes?: Record<string, ITickerChange>;
}

interface IParentEvent {
  id: number;
  company: string;
  event: string;
  summary: string;
  date: string;
  generation_timestamp: string | null;
}

interface IDebugData {
  events_unfetched: number;
  events_batch_comments?: number;
  preopen_events_time?: string;
  last_comment_time: string | null;
}

interface RedditSearchProps {
  panelApi?: DockviewPanelApi;
  containerApi?: DockviewApi;
  isMobile?: boolean;
}

interface ICommentNode {
  text: string;
  marks: Mark[];
  dt_str: string;
  level: number;
}

interface ISearchResult {
  root_id: string;
  nodes: ICommentNode[];
}

interface ISearchResponse {
  results: ISearchResult[];
  total: number;
  page: number;
  total_pages: number;
}

// Status of the newest day summarized by the automatic daily generation (run by the backend
// once a NY day has ended).
interface IDailyStatus {
  target_date: string | null;
  in_progress: boolean;
  started_at: string | null;
  error: boolean;
  error_message: string | null;
  error_trace: string | null;
}

const TARGET_DATE_STORAGE_KEY = "redditSummaryTargetDate";

// Change indicator is jet black regardless of direction (no up/down color coding here).
const CHART_UP_COLOR = "#0a0a0a";
const CHART_DOWN_COLOR = "#0a0a0a";

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

// Spoken form of an event's free-text date (extracted "as written", e.g. "next week",
// "on July 28th", "2026-11-03"): exact dates become "on M/D", anything else is read as is.
function spokenEventDate(date: string): string {
  const trimmed = date.trim();
  const iso = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) return `on ${parseInt(iso[2], 10)}/${parseInt(iso[3], 10)}`;
  const named = trimmed.match(/^(?:on\s+)?([a-z]{3})[a-z]*\.?\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s+\d{4})?$/i);
  if (named) {
    const month = MONTHS.indexOf(named[1].toLowerCase());
    if (month >= 0) return `on ${month + 1}/${parseInt(named[2], 10)}`;
  }
  return trimmed;
}

function readableEventText(event: IRedditEvent, includeSummary: boolean): string {
  const parts = [event.company, event.event];
  if (includeSummary) parts.push(event.summary);
  return parts.join(". ");
}

type ActiveTab = "feed" | "summary" | "comments";


function makeDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export const RedditSearch = ({ panelApi, containerApi, isMobile }: RedditSearchProps) => {
  const [activeTab, setActiveTab] = React.useState<ActiveTab>("feed");

  // Feed tab state
  const [events, setEvents] = React.useState<IRedditEvent[]>([]);
  const [debugData, setDebugData] = React.useState<IDebugData | null>(null);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [pageInput, setPageInput] = React.useState("1");
  const [pageSizeInput, setPageSizeInput] = React.useState("200");
  const [feedReversed, setFeedReversed] = React.useState(false);
  const [highlightedFeedId, setHighlightedFeedId] = React.useState<number | null>(null);
  const [feedMark, setFeedMark] = React.useState<number | "any">(0);
  const [exactDate, setExactDate] = React.useState(false);

  // Summary tab state
  const [summaryEvents, setSummaryEvents] = React.useState<IRedditEvent[]>([]);
  const [dailyStatus, setDailyStatus] = React.useState<IDailyStatus | null>(null);
  const [isSummaryLoading, setIsSummaryLoading] = React.useState(false);
  const [summaryError, setSummaryError] = React.useState("");
  const [summarySearchQuery, setSummarySearchQuery] = React.useState("");
  const [summaryStartDate, setSummaryStartDate] = React.useState(() =>
    makeDateStr(new Date(new Date().setDate(new Date().getDate() - 1)))
  );
  const [summaryEndDate, setSummaryEndDate] = React.useState("");

  // Summary tab - target-date search state
  const [targetDate, setTargetDate] = React.useState("");
  const [targetDateQuery, setTargetDateQuery] = React.useState("");
  const [targetDateError, setTargetDateError] = React.useState("");

  const todayStr = makeDateStr(new Date());
  const lastMonthStr = makeDateStr(new Date(new Date().setMonth(new Date().getMonth() - 1)));
  const lastWeekStr = makeDateStr(new Date(new Date().setDate(new Date().getDate() - 7)));

  const [eventsSearchQuery, setEventsSearchQuery] = React.useState("");
  const [eventsStartDate, setEventsStartDate] = React.useState(lastWeekStr);
  const [eventsEndDate, setEventsEndDate] = React.useState("");

  const [searchQuery, setSearchQuery] = React.useState("");
  const [searchResults, setSearchResults] = React.useState<ISearchResult[]>([]);
  const [searchTotal, setSearchTotal] = React.useState(0);
  const [searchPage, setSearchPage] = React.useState(1);
  const [searchTotalPages, setSearchTotalPages] = React.useState(1);
  const [isSearchLoading, setIsSearchLoading] = React.useState(false);
  const [isSearchLoadingMore, setIsSearchLoadingMore] = React.useState(false);
  const [searchError, setSearchError] = React.useState("");
  const [searchStartDate, setSearchStartDate] = React.useState(lastMonthStr);
  const [searchEndDate, setSearchEndDate] = React.useState(todayStr);

  // parent events expanded per summary event id
  const [expandedParents, setExpandedParents] = React.useState<Record<number, IParentEvent[] | "loading">>({});
  const [summaryExactDate, setSummaryExactDate] = React.useState(false);
  const [targetExactDate, setTargetExactDate] = React.useState(false);

  // Target date is auto-saved to (and loaded from) the browser as soon as it's typed,
  // independent of the manual "Save config" / "Load config" buttons below.
  useEffect(() => {
    const saved = localStorage.getItem(TARGET_DATE_STORAGE_KEY);
    if (saved) setTargetDate(saved);
  }, []);
  useEffect(() => {
    if (targetDate) localStorage.setItem(TARGET_DATE_STORAGE_KEY, targetDate);
  }, [targetDate]);

  const feedScrollRef = useRef<HTMLDivElement>(null);
  const summaryScrollRef = useRef<HTMLDivElement>(null);
  const commentsScrollRef = useRef<HTMLDivElement>(null);

  const [includeSummary, setIncludeSummary] = React.useState(false);

  const feedReadAloud = useReadAloud([events, includeSummary]);
  const summaryReadAloud = useReadAloud([summaryEvents, includeSummary]);

  const [justSaved, setJustSaved] = React.useState(false);
  const [justLoaded, setJustLoaded] = React.useState(false);
  const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current);
      if (loadedTimeoutRef.current) clearTimeout(loadedTimeoutRef.current);
    };
  }, []);

  const saveConfig = useCallback(() => {
    const config = {
      pageInput,
      pageSizeInput,
      feedReversed,
      feedMark,
      exactDate,
      eventsSearchQuery,
      eventsStartDate,
      eventsEndDate,
      summarySearchQuery,
      summaryStartDate,
      summaryEndDate,
      targetDate,
      targetDateQuery,
      summaryExactDate,
      targetExactDate,
      searchQuery,
      searchStartDate,
      searchEndDate,
      includeSummary,
    };
    localStorage.setItem("redditSearchForm", JSON.stringify(config));
    setJustSaved(true);
    if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current);
    savedTimeoutRef.current = setTimeout(() => setJustSaved(false), 1000);
  }, [
    pageInput,
    pageSizeInput,
    feedReversed,
    feedMark,
    exactDate,
    eventsSearchQuery,
    eventsStartDate,
    eventsEndDate,
    summarySearchQuery,
    summaryStartDate,
    summaryEndDate,
    targetDate,
    targetDateQuery,
    summaryExactDate,
    targetExactDate,
    searchQuery,
    searchStartDate,
    searchEndDate,
    includeSummary,
  ]);

  const loadConfig = useCallback(() => {
    const raw = localStorage.getItem("redditSearchForm");
    if (!raw) return;
    try {
      const config = JSON.parse(raw);
      if (config.pageInput !== undefined) setPageInput(config.pageInput);
      if (config.pageSizeInput !== undefined) setPageSizeInput(config.pageSizeInput);
      if (config.feedReversed !== undefined) setFeedReversed(config.feedReversed);
      if (config.feedMark !== undefined) setFeedMark(config.feedMark);
      if (config.exactDate !== undefined) setExactDate(config.exactDate);
      if (config.eventsSearchQuery !== undefined) setEventsSearchQuery(config.eventsSearchQuery);
      if (config.eventsStartDate !== undefined) setEventsStartDate(config.eventsStartDate);
      if (config.eventsEndDate !== undefined) setEventsEndDate(config.eventsEndDate);
      if (config.summarySearchQuery !== undefined) setSummarySearchQuery(config.summarySearchQuery);
      if (config.summaryStartDate !== undefined) setSummaryStartDate(config.summaryStartDate);
      if (config.summaryEndDate !== undefined) setSummaryEndDate(config.summaryEndDate);
      if (config.targetDate !== undefined) setTargetDate(config.targetDate);
      if (config.targetDateQuery !== undefined) setTargetDateQuery(config.targetDateQuery);
      if (config.summaryExactDate !== undefined) setSummaryExactDate(config.summaryExactDate);
      if (config.targetExactDate !== undefined) setTargetExactDate(config.targetExactDate);
      if (config.searchQuery !== undefined) setSearchQuery(config.searchQuery);
      if (config.searchStartDate !== undefined) setSearchStartDate(config.searchStartDate);
      if (config.searchEndDate !== undefined) setSearchEndDate(config.searchEndDate);
      if (config.includeSummary !== undefined) setIncludeSummary(config.includeSummary);
      setJustLoaded(true);
      if (loadedTimeoutRef.current) clearTimeout(loadedTimeoutRef.current);
      loadedTimeoutRef.current = setTimeout(() => setJustLoaded(false), 1000);
    } catch {
      // ignore malformed/legacy stored config
    }
  }, []);

  useEffect(() => {
    fetchLatestEvents();
  }, []);

  // Fetch summary data when the Summary tab is selected. "Exact date" only applies on the
  // next Search (filtered in Postgres), toggling it doesn't re-fetch.
  useEffect(() => {
    if (activeTab === "summary") {
      fetchSummaryEvents(summarySearchQuery);
      fetchDailyStatus();
    }
  }, [activeTab]);

  const fetchLatestEvents = async () => {
    setErrorMessage("");
    setIsLoading(true);
    try {
      const page = Math.max(1, parseInt(pageInput, 10) || 1);
      const pageSize = Math.max(1, parseInt(pageSizeInput, 10) || 200);
      const params: Record<string, string | number | boolean> = { query: "", page, page_size: pageSize, reversed: feedReversed };
      if (feedMark !== "any") params.mark = feedMark;
      if (eventsStartDate) params.start_date = eventsStartDate;
      if (eventsEndDate) params.end_date = eventsEndDate;
      if (exactDate) params.exact_date = true;

      const [eventsResp, analysisResp] = await Promise.all([
        api.get<{ results: IRedditEvent[]; page: number; has_next: boolean }>("/reddit-events-search", { params }),
        api.get<{ analysis: IRedditEvent[]; debug: IDebugData }>("/reddit-analysis"),
      ]);
      setEvents(eventsResp.data.results);
      setDebugData(analysisResp.data.debug);
    } catch (error) {
      console.error("Error fetching reddit analysis:", error);
      setErrorMessage("Error while fetching Reddit analysis");
    } finally {
      setIsLoading(false);
    }
  };

  // Shared by the auto-load (empty query) and the "Search summaries" form - there is no
  // pagination/"Load more" here, the whole matching set is returned in one call.
  const fetchSummaryEvents = async (query = "") => {
    setIsSummaryLoading(true);
    setSummaryError("");
    try {
      const params: Record<string, string | number | boolean> = { query };
      if (summaryStartDate) params.start_date = summaryStartDate;
      if (summaryEndDate) params.end_date = summaryEndDate;
      if (summaryExactDate) params.exact_date = true;
      const resp = await api.get<{ results: IRedditEvent[] }>("/reddit-summary-search", { params });
      setSummaryEvents(resp.data.results);
    } catch (error) {
      console.error("Error fetching summary events:", error);
      setSummaryError("Error while fetching summary events");
    } finally {
      setIsSummaryLoading(false);
    }
  };

  const fetchDailyStatus = async () => {
    try {
      const resp = await api.get<IDailyStatus>("/reddit-summary/daily-status");
      setDailyStatus(resp.data);
    } catch (error) {
      console.error("Error fetching daily summary status:", error);
    }
  };

  const searchRedditEvents = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");
    setIsLoading(true);
    try {
      const page = Math.max(1, parseInt(pageInput, 10) || 1);
      const pageSize = Math.max(1, parseInt(pageSizeInput, 10) || 200);
      const params: Record<string, string | number | boolean> = { query: eventsSearchQuery, page, page_size: pageSize, reversed: feedReversed };
      if (feedMark !== "any") params.mark = feedMark;
      if (eventsStartDate) params.start_date = eventsStartDate;
      if (eventsEndDate) params.end_date = eventsEndDate;
      if (exactDate) params.exact_date = true;
      const response = await api.get<{ results: IRedditEvent[]; page: number; has_next: boolean }>("/reddit-events-search", { params });
      setEvents(response.data.results);
    } catch (error) {
      console.error("Error searching Reddit events:", error);
      setErrorMessage("Error while searching Reddit events");
    } finally {
      setIsLoading(false);
    }
  };

  const searchSummaryEvents = (e: React.FormEvent) => {
    e.preventDefault();
    fetchSummaryEvents(summarySearchQuery);
  };

  function isValidDateOnlyStr(s: string): boolean {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (!m) return false;
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    if (month < 1 || month > 12) return false;
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  }

  // Summaries stamped inside the target NY day. query uses the same to_tsquery('english', ...)
  // syntax as "Search summaries" above (e.g. "! earnings" excludes that term); exactDate keeps
  // only summaries with something in their date field (filtered in Postgres).
  const searchSummariesForDate = async (targetDateValue: string, query: string, exactDate: boolean) => {
    setIsSummaryLoading(true);
    setSummaryError("");
    try {
      const params: Record<string, string | boolean> = { target_date: targetDateValue, query };
      if (exactDate) params.exact_date = true;
      const resp = await api.get<{ results: IRedditEvent[] }>("/reddit-summary/date-search", { params });
      setSummaryEvents(resp.data.results);
    } catch (error) {
      console.error("Error searching summaries for date:", error);
      setSummaryError("Error while searching summaries for that date");
    } finally {
      setIsSummaryLoading(false);
    }
  };

  const handleTargetDateSearch = () => {
    setTargetDateError("");
    const d = targetDate.trim();
    if (!isValidDateOnlyStr(d)) {
      setTargetDateError("Invalid date — use YYYY-MM-DD.");
      return;
    }
    searchSummariesForDate(d, targetDateQuery, targetExactDate);
  };

  const handleOpenSimWatchlist = () => {
    setTargetDateError("");
    const d = targetDate.trim();
    if (!isValidDateOnlyStr(d)) {
      setTargetDateError("Invalid date — use YYYY-MM-DD.");
      return;
    }
    if (!containerApi) return;
    addFloatingPanel(containerApi, "simulate_watchlist", { targetDate: d });
  };

  // poll the daily status while a generation runs, then refresh the list once it's done
  const dailyInProgress = dailyStatus?.in_progress ?? false;
  const prevDailyInProgressRef = useRef(false);
  useEffect(() => {
    if (!dailyInProgress) {
      if (prevDailyInProgressRef.current) fetchSummaryEvents(summarySearchQuery);
      prevDailyInProgressRef.current = false;
      return;
    }
    prevDailyInProgressRef.current = true;
    const interval = setInterval(fetchDailyStatus, 5000);
    return () => clearInterval(interval);
  }, [dailyInProgress]);

  const toggleParents = async (eventId: number, parentIds: number[]) => {
    if (expandedParents[eventId]) {
      setExpandedParents((prev) => { const next = { ...prev }; delete next[eventId]; return next; });
      return;
    }
    if (!parentIds.length) return;
    setExpandedParents((prev) => ({ ...prev, [eventId]: "loading" }));
    try {
      const resp = await api.get<{ results: IParentEvent[] }>("/reddit-events-by-ids", {
        params: { ids: parentIds.join(",") },
      });
      setExpandedParents((prev) => ({ ...prev, [eventId]: resp.data.results }));
    } catch {
      setExpandedParents((prev) => { const next = { ...prev }; delete next[eventId]; return next; });
    }
  };

  function minutesAgo(isoStr: string | null): string {
    if (!isoStr) return "";
    const normalized = isoStr.includes("+") || isoStr.toUpperCase().includes("Z") ? isoStr : isoStr + "Z";
    const mins = Math.floor((Date.now() - new Date(normalized).getTime()) / 60000);
    return `${mins} min ago`;
  }

  const searchReddit = async (e: React.FormEvent, overridePage?: number, append = false) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    const targetPage = overridePage ?? 1;
    setSearchError("");
    if (append) setIsSearchLoadingMore(true); else setIsSearchLoading(true);
    try {
      const response = await api.get<ISearchResponse>("/reddit-search", {
        params: { query: searchQuery, start_date: searchStartDate, end_date: searchEndDate, page: targetPage },
      });
      setSearchResults((prev) => (append ? [...prev, ...response.data.results] : response.data.results));
      setSearchTotal(response.data.total);
      setSearchPage(response.data.page);
      setSearchTotalPages(response.data.total_pages);
    } catch (error) {
      console.error("Error searching Reddit:", error);
      setSearchError("Error while searching Reddit comments");
    } finally {
      if (append) setIsSearchLoadingMore(false); else setIsSearchLoading(false);
    }
  };

  const loadMoreComments = () => {
    const syntheticEvent = { preventDefault: () => {} } as React.FormEvent;
    searchReddit(syntheticEvent, searchPage + 1, true);
  };

  const markEvent = useCallback(
    (id: number, newMark: number) => {
      const cycled = newMark % 3;
      const prevMark = events.find((e) => e.id === id)?.mark ?? 0;
      setEvents((prev) => prev.map((e) => (e.id === id ? { ...e, mark: cycled } : e)));
      api
        .post("/reddit-analysis/mark", { ids: [id], mark: cycled })
        .catch((error) => {
          console.error("Error marking reddit event:", error);
          setEvents((prev) => prev.map((e) => (e.id === id ? { ...e, mark: prevMark } : e)));
        });
    },
    [events]
  );

  const markSummaryEvent = useCallback(
    (id: number, newMark: number) => {
      const cycled = newMark % 3;
      const summaryEvent = summaryEvents.find((e) => e.id === id);
      const prevMark = summaryEvent?.mark ?? 0;
      setSummaryEvents((prev) => prev.map((e) => (e.id === id ? { ...e, mark: cycled } : e)));
      api
        .post("/reddit-summary/mark", null, { params: { id, mark: cycled } })
        .catch((error) => {
          console.error("Error marking summary event:", error);
          setSummaryEvents((prev) => prev.map((e) => (e.id === id ? { ...e, mark: prevMark } : e)));
        });
      if (cycled > 0 && summaryEvent?.parent_ids?.length) {
        const parentIds = summaryEvent.parent_ids;
        setEvents((prev) => prev.map((e) => parentIds.includes(e.id) ? { ...e, mark: 1 } : e));
        api
          .post("/reddit-analysis/mark", { ids: parentIds, mark: 1 })
          .catch((error) => {
            console.error("Error marking source events:", error);
            setEvents((prev) => prev.map((e) => parentIds.includes(e.id) ? { ...e, mark: 0 } : e));
          });
      }
    },
    [summaryEvents]
  );

  const handleCtlD = useCallback(() => {
    if (activeTab === "summary") {
      const firstUnmarked = summaryEvents.find((e) => e.mark === 0);
      if (firstUnmarked) markSummaryEvent(firstUnmarked.id, 1);
    } else {
      const firstUnmarked = events.find((e) => e.mark === 0);
      if (firstUnmarked) markEvent(firstUnmarked.id, 1);
    }
  }, [activeTab, events, summaryEvents, markEvent, markSummaryEvent]);

  const getNextScrollKey = useCallback(() => {
    if (activeTab === "summary") {
      const unmarked = summaryEvents.filter((e) => e.mark === 0);
      return unmarked[1] ? `summary-${unmarked[1].id}` : null;
    }
    const unmarked = events.filter((e) => e.mark === 0);
    return unmarked[1] ? `reddit-${unmarked[1].id}` : null;
  }, [activeTab, events, summaryEvents]);

  useCtrlDMark({ onTrigger: handleCtlD, getNextScrollKey, panelApi, containerApi });

  const feedReadableItems: ReadableItem[] = events
    .filter((e) => e.mark === 0)
    .map((event) => ({
      key: `reddit-${event.id}`,
      texts: [readableEventText(event, includeSummary)],
      onTextDone: () => markEvent(event.id, 1),
    }));

  const summaryReadableItems: ReadableItem[] = summaryEvents
    .filter((e) => e.mark === 0)
    .map((event) => ({
      key: `summary-${event.id}`,
      texts: [readableEventText(event, includeSummary)],
      onTextDone: () => markSummaryEvent(event.id, 1),
    }));

  const onFeedItemDone = useCallback(
    (key: string) => {
      const id = parseInt(key.replace("reddit-", ""), 10);
      const ev = events.find((e) => e.id === id);
      if (ev && ev.mark === 0) markEvent(id, 1);
    },
    [events, markEvent]
  );

  const onSummaryItemDone = useCallback(
    (key: string) => {
      const id = parseInt(key.replace("summary-", ""), 10);
      const ev = summaryEvents.find((e) => e.id === id);
      if (ev && ev.mark === 0) markSummaryEvent(id, 1);
    },
    [summaryEvents, markSummaryEvent]
  );

  function addMarkClass(mark: number): string {
    switch (mark) {
      case 0: return "bg-gray-400";
      case 1: return "bg-blue-600";
      case 2: return "bg-yellow-600";
      default: return "bg-gray-400";
    }
  }

  const formatTimestamp = formatIsoTimestampNYC;

  const scrollToFeedEvent = useCallback((id: number | string) => {
    const numericId = typeof id === "string" ? parseInt(id, 10) : id;
    setHighlightedFeedId(numericId);
    document.getElementById(`reddit-${numericId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => {
      setHighlightedFeedId((prev) => (prev === numericId ? null : prev));
    }, 2500);
  }, []);

  function renderLoadMore(hasMore: boolean, isLoadingMore: boolean, onLoadMore: () => void) {
    if (!hasMore) return null;
    return (
      <div className="flex justify-center my-4">
        <button
          type="button"
          className="btn btn-default disabled:opacity-60 disabled:cursor-default"
          disabled={isLoadingMore}
          onClick={onLoadMore}
        >
          {isLoadingMore ? "Loading…" : "Load more"}
        </button>
      </div>
    );
  }

  function formatSignedPct(v: number): string {
    return `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;
  }

  function renderTickerChange(c: ITickerChange) {
    if (c.change_rel !== undefined) {
      return (
        <span
          className="ml-1 font-semibold tabular-nums"
          style={{ color: c.change_rel >= 0 ? CHART_UP_COLOR : CHART_DOWN_COLOR }}
        >
          {formatSignedPct(c.change_rel)}
        </span>
      );
    }
    if (c.prev_day_change_rel !== undefined && c.premarket_change_rel !== undefined) {
      return (
        <span className="ml-1 font-semibold tabular-nums">
          (
          <span style={{ color: c.prev_day_change_rel >= 0 ? CHART_UP_COLOR : CHART_DOWN_COLOR }}>
            {formatSignedPct(c.prev_day_change_rel)}
          </span>{" "}
          <span style={{ color: c.premarket_change_rel >= 0 ? CHART_UP_COLOR : CHART_DOWN_COLOR }}>
            {formatSignedPct(c.premarket_change_rel)}
          </span>
          )
        </span>
      );
    }
    return null;
  }

  function renderCompanyWithChanges(event: IRedditEvent) {
    if (!event.ticker_changes || Object.keys(event.ticker_changes).length === 0) {
      return <HighlightedText text={event.company} marks={event.company_marks} />;
    }
    const parts = event.company.split(",").map((raw) => raw.trim());
    return parts.map((t, i) => {
      const c = t ? event.ticker_changes?.[t] : undefined;
      return (
        <React.Fragment key={i}>
          {i > 0 && ", "}
          <TickerLink ticker={t} containerApi={containerApi} />
          {c && renderTickerChange(c)}
        </React.Fragment>
      );
    });
  }

  function renderEventList(
    evList: IRedditEvent[],
    onMark: (id: number, mark: number) => void,
    readAloud: ReturnType<typeof useReadAloud>,
    idPrefix: string,
    showParents = false,
    highlightedId: number | null = null
  ) {
    return evList.map((event) => {
      const isReading = readAloud.currentlyReadingKey === `${idPrefix}${event.id}`;
      const isHighlighted = highlightedId === event.id;
      const parentIds = event.parent_ids ?? [];
      const parentState = expandedParents[event.id];
      const isExpanded = !!parentState;
      return (
        <div
          key={event.id}
          id={`${idPrefix}${event.id}`}
          className={`mb-4 flex gap-4 ${isReading || isHighlighted ? "bg-yellow-100" : ""}`}
        >
          <div
            className={`min-w-4 cursor-pointer ${addMarkClass(event.mark)}`}
            onClick={() => onMark(event.id, event.mark + 1)}
          />
          <div
            className="flex-1 flex gap-2"
            onClick={isMobile ? () => onMark(event.id, event.mark + 1) : undefined}
          >
            <div className="flex-1">
              <p className="text-gray-500 text-sm mb-1 flex flex-wrap gap-2">
                <span>{formatTimestamp(event.generation_timestamp)}</span>
                {event.date && <span className="font-semibold">Event: {event.date}</span>}
                {event.num_comments > 0 && (
                  <span className="text-xs text-gray-400">
                    {event.num_comments} comment{event.num_comments !== 1 ? "s" : ""}
                  </span>
                )}
                {showParents && parentIds.length > 0 && (
                  <span className="text-xs text-gray-400">{parentIds.length} source{parentIds.length !== 1 ? "s" : ""}</span>
                )}
              </p>
              <p className="font-bold">
                {renderCompanyWithChanges(event)}
              </p>
              <p className="font-semibold text-gray-800">
                <HighlightedText text={event.event} marks={event.event_marks} />
              </p>
              <p className="whitespace-pre-wrap text-gray-700 mt-1">
                <HighlightedText text={event.summary} marks={event.summary_marks} />
              </p>

              {showParents && parentIds.length > 0 && (
                <>
                  <button
                    type="button"
                    className="mt-2 text-xs text-gray-400 hover:text-gray-700"
                    onClick={(e) => { e.stopPropagation(); toggleParents(event.id, parentIds); }}
                  >
                    {isExpanded ? "▲ Hide sources" : `▼ Show ${parentIds.length} source${parentIds.length !== 1 ? "s" : ""}`}
                  </button>
                  {isExpanded && (
                    <div className="mt-2 border-l-2 border-gray-300 pl-3 space-y-2">
                      {parentState === "loading" ? (
                        <p className="text-xs text-gray-400">Loading…</p>
                      ) : (parentState as IParentEvent[]).map((p) => (
                        <div key={p.id} className="text-sm">
                          <p className="text-gray-400 text-xs">{formatTimestamp(p.generation_timestamp)}{p.date ? ` · ${p.date}` : ""}</p>
                          <p className="font-semibold">{p.company}</p>
                          <p className="text-gray-800">{p.event}</p>
                          <p className="text-gray-600 whitespace-pre-wrap">{p.summary}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            <button
              type="button"
              className="self-start mt-1 mr-3 text-gray-400 hover:text-gray-700 flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                speechSynthesis.cancel();
                const u = new SpeechSynthesisUtterance(event.summary);
                u.voice =
                  speechSynthesis.getVoices().find((v) => v.name === readAloud.voiceControls.voice) ?? null;
                u.rate = readAloud.voiceControls.speed;
                u.pitch = readAloud.voiceControls.pitch;
                speechSynthesis.speak(u);
              }}
              title="Read aloud"
            >
              <MdHeadphones size={18} />
            </button>
          </div>
        </div>
      );
    });
  }

  const activeReadAloud = activeTab === "summary" ? summaryReadAloud : feedReadAloud;
  const activeReadableItems = activeTab === "summary" ? summaryReadableItems : feedReadableItems;
  const activeOnItemDone = activeTab === "summary" ? onSummaryItemDone : onFeedItemDone;
  const activeIdPrefix = activeTab === "summary" ? "summary-" : "reddit-";

  return (
    <div className={`relative flex flex-col h-full ${isMobile ? "w-full" : ""}`}>
      {/* Tab header */}
      <div className="flex-shrink-0 flex items-center gap-4 px-2 py-1.5 border-b border-gray-200">
        {isMobile && (
          <Link to="/mobile" className="btn btn-default">
            To Menu
          </Link>
        )}
        <form className="flex items-center gap-4" onSubmit={(e) => e.preventDefault()}>
        <label className="flex items-center gap-1.5 cursor-pointer select-none text-sm">
          <input
            type="radio"
            name="reddit-tab"
            value="feed"
            checked={activeTab === "feed"}
            onChange={() => setActiveTab("feed")}
          />
          Feed
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer select-none text-sm">
          <input
            type="radio"
            name="reddit-tab"
            value="summary"
            checked={activeTab === "summary"}
            onChange={() => setActiveTab("summary")}
          />
          Summary
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer select-none text-sm">
          <input
            type="radio"
            name="reddit-tab"
            value="comments"
            checked={activeTab === "comments"}
            onChange={() => setActiveTab("comments")}
          />
          Comments
        </label>
        </form>
        <div className="flex items-center gap-2 ml-auto">
          <label className="flex items-center gap-1.5 cursor-pointer select-none text-sm" title="Include full summary in read-aloud">
            <input
              type="checkbox"
              checked={includeSummary}
              onChange={(e) => setIncludeSummary(e.target.checked)}
            />
            Summary
          </label>
        </div>
      </div>

      {/* Feed tab */}
      <div
        ref={feedScrollRef}
        className={`flex-1 overflow-auto p-2 ${activeTab !== "feed" ? "hidden" : ""}`}
      >
        <form className="flex flex-wrap gap-2 items-end mb-4" onSubmit={searchRedditEvents}>
          <div className="form-group w-[260px] mb-0">
            <label htmlFor="events-search-query">Search events:</label>
            <input
              type="text"
              id="events-search-query"
              value={eventsSearchQuery}
              onChange={(e) => setEventsSearchQuery(e.target.value)}
              placeholder="Search Reddit events…"
            />
          </div>
          <div className="form-group mb-0">
            <label htmlFor="events-search-start">Start:</label>
            <input
              type="text"
              id="events-search-start"
              value={eventsStartDate}
              onChange={(e) => setEventsStartDate(e.target.value)}
              placeholder="YYYY-MM-DD"
              className="w-[110px]"
            />
          </div>
          <div className="form-group mb-0">
            <label htmlFor="events-search-end">End:</label>
            <input
              type="text"
              id="events-search-end"
              value={eventsEndDate}
              onChange={(e) => setEventsEndDate(e.target.value)}
              placeholder="YYYY-MM-DD"
              className="w-[110px]"
            />
          </div>
          <div className="form-group mb-0">
            <label htmlFor="events-search-page">Page:</label>
            <input
              type="text"
              id="events-search-page"
              inputMode="numeric"
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              className="w-[70px]"
            />
          </div>
          <div className="form-group mb-0">
            <label htmlFor="events-search-page-size">Page size:</label>
            <input
              type="text"
              id="events-search-page-size"
              inputMode="numeric"
              value={pageSizeInput}
              onChange={(e) => setPageSizeInput(e.target.value)}
              className="w-[80px]"
            />
          </div>
          <button type="submit" className="btn btn-default">
            Search
          </button>
          <label className="flex items-center gap-1.5 cursor-pointer select-none text-sm mb-0">
            <input
              type="checkbox"
              checked={feedReversed}
              onChange={(e) => setFeedReversed(e.target.checked)}
            />
            Reverse
          </label>
          <label className="flex items-center gap-1.5 select-none text-sm mb-0">
            Marked
            <select
              value={feedMark}
              onChange={(e) => setFeedMark(e.target.value === "any" ? "any" : Number(e.target.value))}
              className="text-sm"
            >
              <option value={0}>0</option>
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value="any">any</option>
            </select>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer select-none text-sm mb-0">
            <input
              type="checkbox"
              checked={exactDate}
              onChange={(e) => setExactDate(e.target.checked)}
            />
            Exact date
          </label>
          <button
            type="button"
            className="btn btn-default"
            title="Set all events shown here to unread (frontend only, nothing is saved) to listen to them again"
            onClick={() => setEvents((prev) => prev.map((e) => ({ ...e, mark: 0 })))}
          >
            Unread all
          </button>
          <div className="flex gap-2 w-full">
            <button
              type="button"
              className={`btn ${justSaved ? "btn-primary" : "btn-default"} flex-1`}
              title="Save the current search fields to this browser"
              onClick={saveConfig}
            >
              {justSaved ? "Saved" : "Save config"}
            </button>
            <button
              type="button"
              className={`btn ${justLoaded ? "btn-primary" : "btn-default"} flex-1`}
              title="Load search fields previously saved in this browser"
              onClick={loadConfig}
            >
              {justLoaded ? "Loaded" : "Load config"}
            </button>
          </div>
        </form>

        {isLoading && <div className="text-center">Loading...</div>}

        <RedditFeedTimeline
          items={events.map((e) => ({
            id: e.id,
            generation_timestamp: e.generation_timestamp,
            label: `${e.company} — ${e.event}`,
            eventDate: e.date,
            numComments: e.num_comments,
          }))}
          onItemClick={scrollToFeedEvent}
        />

        {debugData && (
          <div className="text-xs text-gray-500 mb-4 space-y-0.5">
            <p>
              Events unfetched: {debugData.events_unfetched} comments
              {debugData.events_batch_comments != null && (
                <span>
                  {" "}· runs at {debugData.events_batch_comments}
                  {debugData.preopen_events_time && <> · weekdays {debugData.preopen_events_time} NY once (any count)</>}
                </span>
              )}
            </p>
            <p>Last comment time: {formatTimestamp(debugData.last_comment_time)}</p>
          </div>
        )}

        {errorMessage && <p className="text-center text-red-500">{errorMessage}</p>}
        {!isLoading && !errorMessage && events.length === 0 && (
          <p className="text-center">No Reddit events found</p>
        )}

        <div className="mb-20">
          {renderEventList(events, markEvent, feedReadAloud, "reddit-", false, highlightedFeedId)}
        </div>
      </div>

      {/* Summary tab */}
      <div
        ref={summaryScrollRef}
        className={`flex-1 overflow-auto p-2 ${activeTab !== "summary" ? "hidden" : ""}`}
      >
        {/* Section 1: automatic daily generation status */}
        <div className="mb-4 pb-4 border-b border-gray-200 text-sm text-gray-600">
          <p>Summaries are generated automatically for each NY day once it has ended.</p>
          {dailyStatus?.target_date && (
            <p className={dailyStatus.error ? "text-red-600" : dailyStatus.in_progress ? "text-blue-600" : ""}>
              Latest day {dailyStatus.target_date}:{" "}
              {dailyStatus.in_progress
                ? `generating — started ${minutesAgo(dailyStatus.started_at)}`
                : dailyStatus.error
                ? `failed — ${dailyStatus.error_message ?? "unknown error"}`
                : "done"}
            </p>
          )}
          {dailyStatus?.error && dailyStatus.error_trace && (
            <pre className="text-xs text-red-600 overflow-auto max-h-40 whitespace-pre-wrap break-all mt-1">{dailyStatus.error_trace}</pre>
          )}
        </div>

        {/* Section 2: Search summaries */}
        <form
          className="flex flex-wrap gap-2 items-end mb-4 pb-4 border-b border-gray-200"
          onSubmit={searchSummaryEvents}
        >
          <div className="form-group w-[260px] mb-0">
            <label htmlFor="summary-search-query">Search summaries:</label>
            <input
              type="text"
              id="summary-search-query"
              value={summarySearchQuery}
              onChange={(e) => setSummarySearchQuery(e.target.value)}
              placeholder="Search summary events…"
            />
          </div>
          <div className="form-group mb-0">
            <label htmlFor="summary-search-start">Start:</label>
            <input
              type="text"
              id="summary-search-start"
              value={summaryStartDate}
              onChange={(e) => setSummaryStartDate(e.target.value)}
              placeholder="YYYY-MM-DD"
              className="w-[110px]"
            />
          </div>
          <div className="form-group mb-0">
            <label htmlFor="summary-search-end">End:</label>
            <input
              type="text"
              id="summary-search-end"
              value={summaryEndDate}
              onChange={(e) => setSummaryEndDate(e.target.value)}
              placeholder="YYYY-MM-DD"
              className="w-[110px]"
            />
          </div>
          <label className="flex items-center gap-1.5 h-9 mb-0 text-sm text-gray-500 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={summaryExactDate}
              onChange={(e) => setSummaryExactDate(e.target.checked)}
              className="cursor-pointer"
            />
            Exact date
          </label>
          <button type="submit" className="btn btn-default">
            Search
          </button>
        </form>

        {/* Section 3: Generate / search a specific target date */}
        <form
          className="flex items-center gap-2 mb-4 flex-wrap"
          onSubmit={(e) => {
            e.preventDefault();
            handleTargetDateSearch();
          }}
        >
          <div className="form-group mb-0">
            <label htmlFor="target-date">Target date:</label>
            <input
              type="text"
              id="target-date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              placeholder="YYYY-MM-DD"
              className="w-[110px]"
            />
          </div>
          <div className="form-group w-[260px] mb-0">
            <label htmlFor="target-date-query">Search:</label>
            <input
              type="text"
              id="target-date-query"
              value={targetDateQuery}
              onChange={(e) => setTargetDateQuery(e.target.value)}
              placeholder="Search summary events…"
            />
          </div>
          <label className="flex items-center gap-1.5 mt-4 mb-0 text-sm text-gray-500 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={targetExactDate}
              onChange={(e) => setTargetExactDate(e.target.checked)}
              className="cursor-pointer"
            />
            Exact date
          </label>
          <button type="submit" className="btn btn-default mt-4">
            Search
          </button>
          <button
            type="button"
            className="btn btn-default mt-4"
            onClick={handleOpenSimWatchlist}
          >
            Sim. watchlist
          </button>
        </form>

        {targetDateError && (
          <div className="mb-4 text-red-600 text-sm border border-red-300 rounded p-2 bg-red-50">
            {targetDateError}
          </div>
        )}

        {isSummaryLoading && <div className="text-center">Loading...</div>}
        {summaryError && <p className="text-center text-red-500">{summaryError}</p>}
        {!isSummaryLoading && !summaryError && summaryEvents.length === 0 && (
          <p className="text-center">No summary events found</p>
        )}

        {summaryEvents.length > 0 && (
          <div className="mb-20">
            {renderEventList(summaryEvents, markSummaryEvent, summaryReadAloud, "summary-", true)}
          </div>
        )}
      </div>

      {/* Comments tab */}
      <div
        ref={commentsScrollRef}
        className={`flex-1 overflow-auto p-2 ${activeTab !== "comments" ? "hidden" : ""}`}
      >
        <form className="flex flex-wrap gap-2 items-end mb-4" onSubmit={searchReddit}>
          <div className="form-group w-[260px] mb-0">
            <label htmlFor="reddit-search-query">Search comments:</label>
            <input
              type="text"
              id="reddit-search-query"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search Reddit comments…"
            />
          </div>
          <div className="form-group mb-0">
            <label htmlFor="reddit-search-start">Start:</label>
            <input
              type="text"
              id="reddit-search-start"
              value={searchStartDate}
              onChange={(e) => setSearchStartDate(e.target.value)}
              placeholder="YYYY-MM-DD"
              className="w-[110px]"
            />
          </div>
          <div className="form-group mb-0">
            <label htmlFor="reddit-search-end">End:</label>
            <input
              type="text"
              id="reddit-search-end"
              value={searchEndDate}
              onChange={(e) => setSearchEndDate(e.target.value)}
              placeholder="YYYY-MM-DD"
              className="w-[110px]"
            />
          </div>
          <button type="submit" className="btn btn-default">
            Search
          </button>
        </form>

        {(searchResults.length > 0 || isSearchLoading || searchError) && (
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <p className="text-xs text-gray-500">
                {searchTotal} thread{searchTotal !== 1 ? "s" : ""} found
                {searchTotalPages > 1 && ` — page ${searchPage} of ${searchTotalPages}`}
              </p>
            </div>
            {isSearchLoading && <div className="text-center text-sm">Searching…</div>}
            {searchError && <p className="text-red-500 text-sm">{searchError}</p>}
            {!isSearchLoading &&
              searchResults.map((result) => (
                <div key={result.root_id} className="mb-4 border-l-2 border-blue-300 pl-3">
                  {result.nodes.map((node, i) => (
                    <div
                      key={i}
                      style={{ paddingLeft: node.level * 20 }}
                      className="mb-0.5 text-sm leading-relaxed"
                    >
                      <span className="text-gray-400 mr-1">[{node.dt_str}]</span>
                      <HighlightedText text={node.text} marks={node.marks} />
                    </div>
                  ))}
                </div>
              ))}
            {renderLoadMore(searchPage < searchTotalPages, isSearchLoadingMore, loadMoreComments)}
          </div>
        )}
      </div>

      <ReadAloudBar
        voiceControls={activeReadAloud.voiceControls}
        setVoiceControls={activeReadAloud.setVoiceControls}
        toggleReading={activeReadAloud.toggleReading}
        skipItem={activeReadAloud.skipItem}
        items={activeReadableItems}
        onItemDone={activeOnItemDone}
        idPrefix={activeIdPrefix}
        containerApi={containerApi}
        panelApi={panelApi}
      />
    </div>
  );
};
